import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./QRManager.css";
import DocumentQrPanel from "./DocumentQrPanel";
import { AuthImage } from "../lib/authFile";

const QRManager = () => {
  const navigate = useNavigate();
  
  // --- STATE TAB　交換 ---
  const [activeTab, setActiveTab] = useState("employee"); // "employee" または "pdf"

  // --- STATE１社員のQRコード ---
  const [employees, setEmployees] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [generating, setGenerating] = useState(false);
  const [qrResults, setQrResults] = useState([]);
  const [genMsg, setGenMsg] = useState("");
  const [genError, setGenError] = useState("");

  const [options, setOptions] = useState({
    qr_show_memberId: true,
    qr_show_name: true,
    qr_show_dept: true,
    qr_show_position: true,
    qr_show_photo: false,
  });
  const [settingSaved, setSettingSaved] = useState(false);

  // --- STATE１社員のQRコード ---
  useEffect(() => {
    api
      .get("/employees/")
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data?.results || [];
        setEmployees(list);
        if (list.length === 0) setLoadError("社員が登録されていません。");
        else setLoadError("");
      })
      .catch((err) => {
        setEmployees([]);
        setLoadError(
          err?.response?.data?.detail || "社員一覧の取得に失敗しました。ネットワーク接続を確認してください。"
        );
      });
  }, []);

  useEffect(() => {
    api
      .get("/employee-card/display-setting/")
      .then((res) => {
        const d = res.data;
        setOptions({
          qr_show_memberId: d.qr_show_memberId ?? true,
          qr_show_name: d.qr_show_name ?? true,
          qr_show_dept: d.qr_show_dept ?? true,
          qr_show_position: d.qr_show_position ?? true,
          qr_show_photo: d.qr_show_photo ?? false,
        });
      })
      .catch(() => {});
  }, []);

  const employeeKey = (emp) => emp.memberId || emp.id;

  const allChecked = useMemo(() => {
    if (employees.length === 0) return false;
    return employees.every((e) => checkedIds.has(employeeKey(e)));
  }, [employees, checkedIds]);

  const handleToggleAllEmployees = (e) => {
    const on = e.target.checked;
    if (on) {
      setCheckedIds(new Set(employees.map((emp) => employeeKey(emp))));
    } else {
      setCheckedIds(new Set());
    }
  };

  const handleToggleOne = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleOptionChange = (e) => {
    const { name, checked } = e.target;
    setOptions((prev) => ({ ...prev, [name]: checked }));
    setSettingSaved(false);
  };

  const handleSaveSettings = async () => {
    try {
      await api.put("/employee-card/display-setting/update/", options);
      setSettingSaved(true);
      setTimeout(() => setSettingSaved(false), 3000);
    } catch (err) {
      console.error("設定の保存に失敗しました:", err);
    }
  };

  const extractQrUrl = (data) => {
    if (!data) return null;
    // QR 画像は認証付きの配信 API から取得する（docs/spec_file_access.md）
    if (data.qr_api_url) return data.qr_api_url;
    if (data.employee && data.employee.qr_api_url) return data.employee.qr_api_url;
    return null;
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    const ids = [...checkedIds];
    if (ids.length === 0) return;

    setGenerating(true);
    setQrResults([]);
    setGenMsg("");
    setGenError("");

    try {
      await api.put("/employee-card/display-setting/update/", options);

      const results = [];
      for (let i = 0; i < ids.length; i += 1) {
        const memberId = ids[i];
        try {
          const res = await api.post("/employees/generate-qr/", { memberId });
          const url = extractQrUrl(res.data);
          const name =
            res.data?.name ||
            res.data?.employee?.name ||
            employees.find((x) => employeeKey(x) === memberId)?.name ||
            memberId;
          results.push({
            memberId,
            name,
            qr: url,
            ok: !!url,
            err: url ? null : res.data?.message || "URLなし",
          });
        } catch (err) {
          const detail =
            err?.response?.data?.error ||
            err?.response?.data?.detail ||
            err?.message ||
            "エラー";
          results.push({ memberId, name: memberId, qr: null, ok: false, err: detail });
        }
      }
      setQrResults(results);
      const okn = results.filter((r) => r.ok).length;
      setGenMsg(`${ids.length} 名中 ${okn} 件の QR を取得しました（既存の場合はそのまま表示されます）。`);
      setSettingSaved(true);
      setTimeout(() => setSettingSaved(false), 3000);
    } catch (err) {
      setGenError("一括生成の準備で失敗しました。");
    } finally {
      setGenerating(false);
    }
  };

 
  const OPTION_LABELS = [
    { name: "qr_show_memberId", label: "社員コード" },
    { name: "qr_show_name", label: "氏名" },
    { name: "qr_show_dept", label: "部署" },
    { name: "qr_show_position", label: "役職" },
    { name: "qr_show_photo", label: "社員写真" },
  ];

  return (
    <div className="qr-page">
      <div className="qr-header">
        <button type="button" className="qr-btn-back" onClick={() => navigate("/dashboard")}>
          メニュー画面に戻る
        </button>
        <h2 className="qr-title">QR生成</h2>
      </div>

      
      <div className="qr-tab-buttons">
        <button
          type="button"
          className={`qr-tab-btn ${activeTab === "employee" ? "active" : ""}`}
          onClick={() => setActiveTab("employee")}
        >
          👤 社員QR管理
        </button>
        <button
          type="button"
          className={`qr-tab-btn ${activeTab === "pdf" ? "active" : ""}`}
          onClick={() => setActiveTab("pdf")}
        >
          📄 顧客用PDF QR管理
        </button>
      </div>

      <div className="qr-body">
        
    
        {activeTab === "employee" && (
          <>
            {loadError && <p className="qr-error-msg">{loadError}</p>}

            <form onSubmit={handleBulkGenerate} className="qr-form">
              <div className="qr-field">
                <span className="qr-label">選択社員</span>
                <p className="qr-label-sub" style={{ margin: "0 0 8px" }}>
                  名前の左のチェックで対象を選び、「全ての社員」で一覧の全員を一括選択・解除できます。
                </p>
                <div className="qr-emp-list-wrap">
                  <label className="qr-emp-row qr-emp-row--all">
                    <input type="checkbox" checked={allChecked} onChange={handleToggleAllEmployees} />
                    <span className="qr-emp-all-label">全ての社員</span>
                  </label>
                  <div className="qr-emp-list">
                    {employees.map((emp) => {
                      const id = employeeKey(emp);
                      return (
                        <label key={id} className="qr-emp-row">
                          <input
                            type="checkbox"
                            checked={checkedIds.has(id)}
                            onChange={() => handleToggleOne(id)}
                          />
                          <span className="qr-emp-text">
                            {emp.name || id}（{emp.memberId || id}）
                            {emp.departmentName || emp.dept_name
                              ? ` ${emp.departmentName || emp.dept_name}`
                              : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="qr-field">
                <span className="qr-label">
                  QRスキャン時に表示する項目
                  <span className="qr-label-sub">（スキャン端末で表示される内容）</span>
                </span>
                <div className="qr-checkboxes">
                  {OPTION_LABELS.map(({ name, label }) => (
                    <label key={name} className="qr-checkbox-label">
                      <input type="checkbox" name={name} checked={options[name]} onChange={handleOptionChange} />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="qr-setting-actions">
                  <button type="button" className="qr-btn-save-setting" onClick={handleSaveSettings}>
                    表示設定のみ保存
                  </button>
                  {settingSaved && <span className="qr-setting-saved">✓ 保存しました</span>}
                </div>
              </div>

              <button
                type="submit"
                className="qr-btn-generate"
                disabled={generating || checkedIds.size === 0}
              >
                {generating ? "生成中..." : "選択した社員のQRを一括生成"}
              </button>
            </form>

            {genMsg && <p className="qr-success-msg">{genMsg}</p>}
            {genError && <p className="qr-error-msg">{genError}</p>}

            {qrResults.length > 0 && (
              <div className="qr-bulk-grid">
                {qrResults.map((r) => (
                  <div key={r.memberId} className="qr-bulk-item">
                    <p className="qr-bulk-name">{r.name}</p>
                    <p className="qr-bulk-mid">{r.memberId}</p>
                    {r.ok && r.qr ? (
                      <AuthImage url={r.qr} alt="" className="qr-image qr-bulk-img" />
                    ) : (
                      <p className="qr-error-msg" style={{ fontSize: 12 }}>
                        {r.err || "取得できませんでした"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        
        {/* 顧客用PDF QR：文書ごとに PDF と QR を持つ（複数登録できる） */}
        {activeTab === "pdf" && <DocumentQrPanel />}

        {activeTab === "employee" && (
          <div className="qr-scanner-link-box">
            <p className="qr-scanner-link-desc">QRコードの読み取り・出退勤打刻はこちら</p>
            <button type="button" className="qr-btn-scanner" onClick={() => navigate("/admin/qr-scan")}>
              📷 QRスキャン端末を開く
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRManager;